import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { useT } from "@ec2/i18n/react";
import type { GuideStep } from "../../lib/install-guides";
import type { InstallPlatform } from "../../lib/platform";
import { MockDevice } from "./MockDevice";

const STEP_MS = 3200;

function usePrefersReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/**
 * The animated install guide: one mock-up scene per step, advancing on its own
 * and looping, with the caption under it. Tapping a step (or "Show again")
 * jumps there. Reduced motion: every step at once, nothing moving.
 *
 * Screen readers get the captions as an ordered list; the mock-ups are hidden.
 */
export function Walkthrough({
  steps,
  platform,
}: {
  steps: GuideStep[];
  platform: InstallPlatform;
}) {
  const t = useT();
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced || steps.length < 2) return;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % steps.length), STEP_MS);
    return () => window.clearInterval(timer);
  }, [reduced, steps.length]);

  if (reduced) {
    return (
      <ol className="flex flex-col gap-6" data-testid="ec-install-steps">
        {steps.map((step, i) => (
          <li key={i} className="flex flex-col gap-2">
            <MockDevice scene={step.scene} platform={platform} />
            <p className="text-center text-base text-fg">
              <span className="font-semibold">{i + 1}. </span>
              {t(step.caption)}
            </p>
          </li>
        ))}
      </ol>
    );
  }

  const step = steps[index]!;
  return (
    <div
      className="flex flex-col items-center gap-3"
      data-testid="ec-install-walkthrough"
      data-step={index}
    >
      <ol className="sr-only">
        {steps.map((s, i) => (
          <li key={i}>{t(s.caption)}</li>
        ))}
      </ol>
      <div key={index} className="motion-safe:animate-[ec-fade-in_300ms_ease-out]">
        <MockDevice scene={step.scene} platform={platform} />
      </div>
      <p aria-hidden className="min-h-[3rem] max-w-[34ch] text-center text-base text-fg">
        <span className="font-semibold">{index + 1}. </span>
        {t(step.caption)}
      </p>
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {steps.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={t("ec2.install.stepOf", { current: i + 1, total: steps.length })}
              className="p-1"
              onClick={() => setIndex(i)}
            >
              <span
                className={`block h-2 w-2 rounded-full ${i === index ? "bg-accent" : "bg-fg/20"}`}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[13px] font-medium text-fg-secondary"
          onClick={() => setIndex(0)}
        >
          <RotateCcw size={14} aria-hidden /> {t("ec2.install.replay")}
        </button>
      </div>
    </div>
  );
}
